
<template>
  <div class="home">
    <!-- Hero Carousel -->
    <section class="hero-carousel-section">
      <Swiper
        v-if="carouselBanners.length > 0"
        :modules="[Navigation, Pagination, Autoplay]"
        :slides-per-view="1"
        loop
        pagination
        navigation
        class="mySwiper"
        :autoplay="{ delay: 3000, disableOnInteraction: false }"
      >
        <SwiperSlide
          v-for="(banner, index) in carouselBanners"
          :key="index"
        >
          <div class="banner-slide">
            <img
              class="banner-slide__img"
              :src="getImagePath(bannerImage(banner))"
              :alt="banner.ctitle"
              width="1920"
              height="450"
              fetchpriority="high"
            />
            <div class="banner-overlay">
              <h2>{{ banner.ctitle }}</h2>
              <p>{{ banner.description }}</p>
              <router-link :to="banner.link_url" class="cta-button">
                了解更多
              </router-link>
            </div>
          </div>
        </SwiperSlide>
      </Swiper>
    </section>

    <!-- 檢驗報告區 -->
    <section v-if="report_001" v-html="report_001"></section>

    <!-- 專業美容師區 -->
    <section
      class="professional-beautician-section"
      v-if="beauticiansCurrent.length > 0"
    >
      <h2>專業美容師推薦</h2>
      <div class="beautician-container">
        <div
          class="beautician-marquee"
          :class="{ BeauticianPlaying: isBeauticianPlaying }"
          @animationend="onAnimationCarouselBanner"
        >
          <div
            class="beautician-card"
            v-for="(usersBE, idx) in beauticiansCurrent"
            :key="idx"
          >
            <img
              :src="usersBE.image_url"
              :alt="`${$COMPANY_NAME} (${$COMPANY_NAMEn}) 專業美容師推薦`"
              loading="lazy"
            />
          </div>
        </div>
      </div>
    </section>

    <!-- 用戶真實推薦區 -->
    <section
      class="user-testimonial-section"
      v-if="testimonialsCurrent.length > 0"
    >
      <h2>用戶真實推薦</h2>
      <div class="testimonial-container">
        <div
          class="testimonial-marquee"
          :class="{ TestimonialPlaying: isTestimonialPlaying }"
          @animationend="onAnimationIteration"
        >
          <div
            class="testimonial-card"
            v-for="(user, idx) in testimonialsCurrent"
            :key="idx"
          >
            <div class="testimonial-bg">
              <img
                class="testimonial-image"
                :src="user.image_url"
                :alt="`${$COMPANY_NAME} (${$COMPANY_NAMEn}) 用戶評價：${user.review}`"
                loading="lazy"
              />
              <div class="testimonial-overlay">
                <p class="testimonial-text">{{ user.review }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="home-products-section" v-if="homeProducts.length > 0">
    <!-- 首頁主標題 h1（搜尋引擎用來判斷這頁在講什麼；全頁只能有這一個 h1） -->
    <!-- 第二行小字也屬於 h1（搜尋引擎讀到的是整句），畫面維持「大字一行＋小字一行」 -->
    <h1 class="home-main-title">JiLi 肌黎｜外泌體面膜・安瓶・潔顏慕斯 <span class="home-main-sub">保濕修護保養品推薦</span></h1>

    <!-- 活動公告橫幅 -->
    <div v-for="p in promotions" :key="p.id" class="home-promo-banner">
      <span class="hpb-icon">📣</span>
      <span class="hpb-tag">{{ p.content }}</span>
    </div>

    <!-- 滿額贈＋折扣券：「買越多送越多」階梯公告（直接讀後台「活動管理」，改那邊這裡自動同步） -->
    <PromoLadder :gifts="thresholdGifts" :coupons="coupons" class="home-promo-ladder" />

    <div class="product-list">
      <div v-for="(prod, idx) in homeProducts" :key="idx" class="product-card">
        <router-link :to="`/product/${prod.product_id}`" class="product-image-link">
          <div class="image-wrapper">
            <img :src="prod.image_url" :alt="prod.title" loading="lazy" />
            <!-- 顯示新品徽章 -->
            <span v-if="prod.is_new == 1" class="badge star new">新品</span>
            <!-- 顯示熱銷徽章 -->
            <span v-if="prod.is_hot == 1" class="badge star hot" :class="{'addbadgetop': prod.is_new == 1 && prod.is_hot == 1 }">熱銷</span>
          </div>
        </router-link>
        <div class="product-info">
          <router-link :to="`/product/${prod.product_id}`" class="product-name">
            {{ prod.title }}
          </router-link>
          <p class="product-desc" v-html="highlightDesc(prod.description)"></p>
          <div class="product-pricing">
            <span class="original-price" v-if="prod.price !== prod.discount_price">
              NT${{ prod.price }}
            </span>
            <span class="current-price">NT${{ prod.discount_price }}</span>
            <span v-if="prod.price && prod.discount_price && prod.price !== prod.discount_price"
                  class="home-discount-badge">
              {{ (Math.round(prod.discount_price / prod.price * 100) / 10).toFixed(1).replace(/\.0$/, '') }}折
            </span>
          </div>
          <div v-if="bestCouponFor(prod.discount_price)" class="home-coupon-after">
            🏷️ 券後 <strong>NT${{ Math.round(prod.discount_price * Number(bestCouponFor(prod.discount_price).discount_rate) / 10).toLocaleString() }}</strong>
            <span>（滿{{ bestCouponFor(prod.discount_price).min_amount }}享{{ Number(bestCouponFor(prod.discount_price).discount_rate) }}折）</span>
            <span v-if="bestCouponFor(prod.discount_price).days_left != null" class="home-coupon-expire">⏳ 倒數{{ bestCouponFor(prod.discount_price).days_left }}天</span>
          </div>
          <div class="stock-sold-wrapper" v-if="prod.sold_quantity > 0">
            <div class="info-box sold-box">
              <i class="fas fa-fire-alt"></i>
              <span class="label">已售出&ensp;</span>
              <span class="number">{{ prod.sold_quantity }}</span>
              <span class="label">&ensp;{{ prod.unit }}</span>
            </div>
          </div>
        </div>
        <button class="add-to-cart-button" @click="openCartModal(prod)">
          加入購物車
        </button>
      </div>
    </div>

    <!-- 你的 CartModal 組件 -->
    <CartModal
      v-if="showCartModal"
      :product="product"
      :isMobile="isMobile"
      @close-modal="closeModal"
      @add-to-cart="handleAddToCart"
    />
  </section>

    <!-- 保養推薦指南：首頁直接連到各「XX推薦」主文（首頁份量最大，連過去的文章排名較有幫助） -->
    <nav class="home-guide" aria-label="保養推薦指南">
      <p class="home-guide__title">📖 保養推薦指南</p>
      <div class="home-guide__links">
        <router-link v-for="g in guideLinks" :key="g.id" :to="`/posters/${g.id}`" class="home-guide__link">{{ g.text }}</router-link>
      </div>
    </nav>


    <!-- 證書顯示區 -->
    <section class="certificate-section" v-if="certificates.length > 0">
      <div v-html="home_001"></div>
      <div class="certificate-grid">
        <div
          class="certificate-card"
          v-for="(cert, idx) in certificates"
          :key="idx"
        >
          <img
            :src="cert.image_url"
            loading="lazy"
            :alt="`${$COMPANY_NAME} (${$COMPANY_NAMEn}) ${cert.description}`"
          />
          <h3 class="certificate-title">{{ cert.title }}</h3>
          <p class="certificate-desc">
            {{ cert.description }}
          </p>
        </div>
      </div>
    </section>

    <!-- 品牌故事區 -->
    <section class="brand-story-section">
      <div v-html="home_002"></div>
    </section>
  </div>
</template>
<script setup>
import { defineOptions, ref, onMounted, onBeforeUnmount } from 'vue'
import { loadGifts, loadCoupons } from '@/utils/promoData'
import PromoLadder from '@/components/PromoLadder.vue'
import axios from 'axios'
import { Swiper, SwiperSlide } from 'swiper/vue'
import { Navigation, Pagination, Autoplay } from 'swiper/modules'
import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/pagination'
import 'swiper/css/autoplay'
import CartModal from '@/components/CartModal.vue'
import { useHomeStore } from '@/store/homeStore'
import { useStore } from 'vuex'

defineOptions({
   name: 'HomePage'
})

const store = useStore()

const isMobile = ref(false)
const carouselBanners = ref([])
const usersTestimonials = ref([])
const usersBEs = ref([])
const certificates = ref([])
const homeProducts = ref([])
const isBeauticianPlaying = ref(false)
const beauticiansCurrent = ref([])
const isTestimonialPlaying = ref(false)
const testimonialsCurrent = ref([])
const batchSize = ref(10)
const showCartModal = ref(false)
const product = ref(null)
const home_001 = ref(null)
const home_002 = ref(null)
const report_001 = ref(null)
const coupons = ref([])
const promotions = ref([])
const thresholdGifts = ref([])
// 首頁「保養推薦指南」：連到各推薦主文（文章合併/改負責文章時要同步）
const guideLinks = [
  { id: 10261, text: '面膜推薦' },
  { id: 10011, text: '外泌體面膜推薦' },
  { id: 10013, text: '安瓶推薦' },
  { id: 10039, text: '外泌體安瓶推薦' },
  { id: 10264, text: '潔顏慕斯推薦' },
  { id: 10280, text: '保濕推薦' },
  { id: 10278, text: '新手保養品推薦' },
]

function highlightDesc(text) {
  if (!text) return ''
  return text.replace(/(買\d+[^\s，,、。！!]{0,4}送\d+[^\s，,、。！!]{0,4})/g,
    '<span class="desc-promo">$1</span>')
}

function bestCouponFor(price) {
  const applicable = coupons.value.filter(c => Number(price) >= c.min_amount)
  if (!applicable.length) return null
  // 折數越小 = 折扣越大，選折數最小的
  return applicable.reduce((best, c) => Number(c.discount_rate) < Number(best.discount_rate) ? c : best)
}

function checkMobile() {
  isMobile.value = window.innerWidth <= 768
}

function openCartModal(prod) {
  product.value = prod
  showCartModal.value = true
  document.body.classList.add('no-scroll')
}

function closeModal() {
  showCartModal.value = false
  document.body.classList.remove('no-scroll')
}

function bannerImage(banner) {
  return isMobile.value ? banner.phone_url : banner.image_url
}

function shuffle(arr) {
  const copy = [...arr]
  let m = copy.length
  while (m) {
    const i = Math.floor(Math.random() * m--)
    const t = copy[m]
    copy[m] = copy[i]
    copy[i] = t
  }
  return copy
}

function nextUsersTestimonialBatch() {
  const shuffled = shuffle(usersTestimonials.value)
  const batch = batchSize.value > shuffled.length ? shuffled.length : batchSize.value
  testimonialsCurrent.value = shuffled.slice(0, batch)
}

function nextCarouselBannerBatch() {
  const shuffled = shuffle(usersBEs.value)
  const batch = batchSize.value > shuffled.length ? shuffled.length : batchSize.value
  beauticiansCurrent.value = shuffled.slice(0, batch)
}

function onAnimationCarouselBanner() {
  isBeauticianPlaying.value = false
  nextCarouselBannerBatch()
  setTimeout(() => {
    isBeauticianPlaying.value = true
  }, 100)
}

function onAnimationIteration() {
  isTestimonialPlaying.value = false
  nextUsersTestimonialBatch()
  setTimeout(() => {
    isTestimonialPlaying.value = true
  }, 100)
}

function getImagePath(path) {
  if (!path) return ''
  return path.startsWith('http') ? path : `/${path}`
}

async function handleAddToCart({ product: prod, quantity }) {
  store.dispatch('addProductToCart', { product: prod, quantity })
  //toastStore.addToast(`成功加入購物車：${prod.title}`)
  closeModal()
}

onMounted(async () => {
  checkMobile()
  window.addEventListener('resize', checkMobile)
  isTestimonialPlaying.value = true
  isBeauticianPlaying.value = true

  const homeStore = useHomeStore()
  await homeStore.fetchHomeData()

  carouselBanners.value = homeStore.carouselBanners
  usersTestimonials.value = homeStore.usersTestimonials
  usersBEs.value = homeStore.usersBEs
  certificates.value = homeStore.certificates
  homeProducts.value = homeStore.homeProducts
  home_001.value = homeStore.home_001
  home_002.value = homeStore.home_002
  report_001.value = homeStore.report_001

  //console.log('home_001', home_001.value)

  nextUsersTestimonialBatch()
  nextCarouselBannerBatch()

  // 折扣券＋滿額贈同時載入、一起顯示（階梯公告才不會先出現一半）
  try {
    const [cs, gs] = await Promise.all([loadCoupons(), loadGifts()])
    coupons.value = cs
    thresholdGifts.value = gs
  } catch (e) { /* 靜默失敗 */ }
  // 載入活動公告
  try {
    const rp = await axios.get('/api/load/promotions')
    if (rp.data.success) promotions.value = rp.data.promotions
  } catch (e) { /* 靜默失敗 */ }
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', checkMobile)
})
</script>

<style>
.no-scroll {
  overflow: hidden;
  height: 100%;
}
</style>

<style scoped>
/* 讓首頁整體寬度、置中、文字對齊 */
.home {
  width: 100%;
  margin: 0 auto;
  text-align: center;
}

/* ------------------------------
   Hero Carousel 區塊：改成滿版
------------------------------ */
.hero-carousel-section {
  width: 100%;
  margin: 0 auto;
}

.mySwiper {
  width: 100%;
  aspect-ratio: 1920 / 450;
  margin: 0 auto;
}

.banner-slide {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.banner-slide__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

@media (max-width: 768px) {
  .mySwiper {
    aspect-ratio: 450 / 450;
  }
}

.banner-overlay {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.3);
  color: #fff;
  padding: 30px;
}

.cta-button {
  display: inline-block;
  margin-top: 20px;
  background-color: #ff6600;
  color: #fff;
  padding: 8px 16px;
  text-decoration: none;
}

/* ----------------------------------------
   專業美容師區 (Professional Beautician)
---------------------------------------- */
.professional-beautician-section {
  background: #fff;
  padding: 30px;
  text-align: center;
}

/* 首頁主標題（原「精選保養品」h2 的位置與大小） */
.home-main-title {
  font-size: 28.8px;
  font-weight: 700;
  color: #000;
  text-align: center;
  margin: 24px 16px 4px;
  line-height: 1.35;
}
.home-main-title {
  margin-bottom: 20px;
}
/* 保養推薦指南 */
.home-guide {
  max-width: 1100px;
  margin: 8px auto 28px;
  padding: 14px 16px;
  text-align: center;
  border-top: 1px dashed #e5d6c6;
  border-bottom: 1px dashed #e5d6c6;
}
.home-guide__title {
  font-weight: 700;
  color: #7a4a1e;
  margin: 0 0 10px;
}
.home-guide__links {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px 10px;
}
.home-guide__link {
  padding: 5px 14px;
  border: 1px solid #e8c9a8;
  border-radius: 999px;
  background: #fffaf4;
  color: #8a4b12;
  font-size: 0.92rem;
  text-decoration: none;
  white-space: nowrap;
}
.home-guide__link:hover {
  background: #ff6600;
  border-color: #ff6600;
  color: #fff;
}

/* h1 裡的第二行小字（屬於同一個 h1） */
.home-main-sub {
  display: block;
  margin-top: 6px;
  font-size: 1.05rem;
  font-weight: 400;
  color: #666;
  letter-spacing: 2px;
}
@media (max-width: 600px) {
  .home-main-title { font-size: 1.35rem; }
}

.professional-beautician-section h2 {
  margin-bottom: 20px;
}

.beautician-container {
  width: 100%;
  overflow: hidden;
  position: relative;
}

.beautician-marquee {
  display: flex;
  transform: translateX(-100%);
}

.beautician-marquee.BeauticianPlaying {
  animation: scrollLeftToRight 15s linear forwards;
}

@keyframes scrollLeftToRight {
  0% {
    transform: translateX(-100%);
  }

  100% {
    transform: translateX(100%);
  }
}

/**
  針對 .beautician-card
  - 使用 aspect-ratio: 1 / 1，確保正方形
  - 或者只要自動縮放，也可 max-width: 350px; width: 100%;
*/
.beautician-card {
  max-width: 350px;
  /* 外層容器最大 350px */
  width: 100%;
  /* 小螢幕時可自動縮小 */
  aspect-ratio: 1 / 1;
  /* 保持正方形比例 (現代瀏覽器) */
  flex-shrink: 0;
  border-radius: 8px;
  overflow: hidden;
  margin: 0 auto;
  position: relative;
}

.beautician-card img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  /* 自動裁切 */
  display: block;
}

/* ----------------------------------------
   用戶真實推薦區 (User Testimonials)
---------------------------------------- */
.user-testimonial-section {
  background: #fafafa;
  padding: 30px;
  text-align: center;
}

.user-testimonial-section h2 {
  margin-bottom: 20px;
}

.testimonial-container {
  width: 80%;
  margin: 0 auto;
  overflow: hidden;
  position: relative;
}

.testimonial-marquee {
  display: flex;
  transform: translateX(100%);
}

.testimonial-marquee.TestimonialPlaying {
  animation: scrollLeft 15s linear forwards;
}

@keyframes scrollLeft {
  0% {
    transform: translateX(100%);
  }

  100% {
    transform: translateX(-100%);
  }
}

.testimonial-card {
  min-width: 200px;
  margin-right: 20px;
  flex-shrink: 0;
  border-radius: 8px;
  overflow: hidden;
  position: relative;
}

.testimonial-bg {
  width: 200px;
  height: 200px;
  position: relative;
}

.testimonial-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.testimonial-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(0, 0, 0, 0.3);
  color: #fff;
  text-align: center;
  padding: 10px;
}

.testimonial-text {
  font-size: 0.9rem;
  line-height: 1.4;
}

/* ------------------------------
   證書區
------------------------------ */
.certificate-section {
  padding: 30px;
  text-align: center;
  background: #fefefe;
}

.certificate-section h2 {
  margin-bottom: 20px;
}

.certificate-intro {
  max-width: 800px;
  margin: 0 auto 20px;
  line-height: 1.6;
  color: #666;
}

.certificate-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  justify-content: center;
}



.certificate-card {
  width: 390px;
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 0 5px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-bottom: 20px;
}

.certificate-card img {
  width: 100%;
  height: auto;
  object-fit: cover;
  border-bottom: 1px solid #eee;
}


.certificate-title {
  margin: 15px 0 5px;
  font-size: 1.1rem;
  font-weight: bold;
  color: #333;
}

.certificate-desc {
  font-size: 0.9rem;
  color: #666;
  padding: 0 10px;
  text-align: left;
}

/* RWD 調整 */
@media (max-width: 768px) {
  .certificate-card {
    width: calc(30% - 10px); /* 手機版仍保持三個 */
    flex: 1 1 calc(30% - 10px);
  }
  .certificate-title {
    font-size: 0.8rem;
  }

  .certificate-desc[data-v-c6bdc170] {
    font-size: 0.6rem;
  }
}

/* ------------------------------
   品牌故事區
------------------------------ */
.brand-story-section {
  padding: 40px 20px;
  background-color: #f5f5f5;
}

.brand-story-section h2 {
  margin-bottom: 20px;
}

.brand-story-section p {
  max-width: 800px;
  margin: 0 auto;
  line-height: 1.6;
}

/* RWD 調整 */
@media (max-width: 768px) {
  .beautician-card {
    /* 針對小螢幕可再縮小 max-width */
    max-width: 200px;
  }
}

.hot-products-section {
  padding: 30px;
}

.hot-products-section h2 {
  margin-bottom: 20px;
}

/* 商品卡片列表容器 */
.product-list {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  justify-content: center;
}

/* ------------------------------
   熱銷產品區
------------------------------ */
.product-card {
  width: 350px;
  background: #fff;
  border-radius: 6px;
  overflow: hidden;
  box-shadow: 0 0 6px rgba(0, 0, 0, 0.1);
  text-align: center;
  position: relative;
  padding-bottom: 10px;
}

.product-card img {
  width: 100%;
  height: 300px;
  object-fit: cover;
  display: block;
  transition: transform 0.3s;
}

.product-card:hover img {
  transform: scale(1.15);
}

.product-info {
  padding: 35px;
  text-align: left;
}

.product-name {
  font-weight: bold;
  color: #333;
  text-decoration: none;
  display: inline-block;
  margin-bottom: 5px;
}

.product-name:hover {
  color: #007bff;
}

.product-desc {
  font-size: 0.9rem;
  color: #666;
  margin-bottom: 8px;
  line-height: 1.6;
}
.product-desc :deep(.desc-promo) {
  display: inline-block;
  background: linear-gradient(135deg, #ff6600, #ff9900);
  color: #fff;
  font-weight: 700;
  font-size: 0.82rem;
  padding: 1px 8px;
  border-radius: 20px;
  margin: 0 2px;
  white-space: nowrap;
  box-shadow: 0 1px 4px rgba(255,102,0,0.3);
}

.product-pricing .original-price {
  font-size: 1.2rem;
  text-decoration: line-through;
  color: #999;
  margin-right: 8px;
}

.product-pricing .current-price {
  font-size: 1.6rem;
  color: red;
  font-weight: bold;
}

/* 活動公告橫幅 */
.home-promo-banner {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  background: linear-gradient(90deg, #fff8f0 0%, #fff3e0 100%);
  border: 1px solid #f0c060;
  border-radius: 8px;
  padding: 10px 18px;
  margin-bottom: 6px;
  font-size: 0.93rem;
}
.hpb-icon { font-size: 1.1rem; }
.hpb-tag  { color: #cc4400; font-weight: 600; }

/* 折扣券橫幅 */
.home-coupon-banner {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 6px;
  background: linear-gradient(90deg, #fff3e8, #fff8f3);
  border: 1.5px solid #ffb347;
  border-radius: 10px;
  padding: 9px 16px;
  margin-bottom: 18px;
  font-size: 0.9rem;
}
.hcb-label { font-weight: 700; color: #cc4400; }
.hcb-tag   { color: #aa3300; font-weight: 600; }
.hcb-expire { color: #c0392b; font-size: 0.82rem; font-weight: 700; }
.home-gift-banner { background: linear-gradient(90deg, #eefaf1, #f6fcf8); border-color: #7cc79a; }
.home-gift-banner .hcb-label { color: #1f7a45; }
.home-gift-banner .hcb-tag   { color: #1f6b3c; }

/* 券後價 */
.home-coupon-after {
  font-size: 0.8rem;
  color: #cc4400;
  margin-top: 3px;
  display: flex;
  align-items: center;
  gap: 3px;
  flex-wrap: wrap;
}
.home-coupon-after strong { font-size: 0.95rem; }
.home-coupon-after span   { color: #bbb; font-size: 0.73rem; }
.home-coupon-expire { color: #c0392b !important; font-size: 0.73rem !important; font-weight: 700; }
.home-discount-badge {
  display: inline-block;
  background: #e60000;
  color: #fff;
  font-size: 0.78rem;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 4px;
  margin-left: 5px;
  vertical-align: middle;
}

.add-to-cart-button {
  border: none;
  background-color: #ff6600;
  color: #fff;
  padding: 8px 12px;
  cursor: pointer;
  border-radius: 4px;
  font-size: 0.9rem;
}

.add-to-cart-button:hover {
  background-color: #ff5500;
}


.image-wrapper {
  position: relative;
}

/* 基礎徽章樣式 */
.badge {
  /* 把徽章改大一點 */
  position: absolute;
  top: 10px;
  right: 10px;
  display: flex;     /* 讓文字可置中 */
  justify-content: center;
  align-items: center;
  color: #fff;
  font-size: 1rem;   /* 文字大一點 */
  font-weight: bold; /* 文字粗一點 */
  text-align: center;
  z-index: 1;
  /* 你可以再加點陰影 */
  box-shadow: 0 3px 5px rgba(0,0,0,0.3);
  /* 若要圓潤可加 border-radius: 4px; */
}



/* 新品徽章樣式 */
.badge.new {
  background-color: #27ae60; /* 可調整為你喜歡的顏色 */
}

/* 熱銷徽章樣式 */
.badge.hot {
  background-color: #e74c3c;
  color: #ffd600;           /* 黃色文字 */
}


.addbadgetop{
  top: 70px;
}

/* 星形關鍵：使用 clip-path */
.badge.star {
  position: absolute;
  right: 10px;
  width: 65px; 
  height: 55px;
  font-size: 1.2rem;
  font-weight: bold;
  text-align: center;
  line-height: 120px;       /* 讓文字置中 */
  z-index: 1;
  clip-path: polygon(
    45% 0%,   /* 多邊形點：可依需求做更多鋸齒 */
    60% 10%,
    90% 0%,
    80% 30%,
    100% 50%,
    80% 70%,
    90% 100%,
    60% 90%,
    45% 100%,
    40% 90%,
    10% 100%,
    20% 70%,
    0% 50%,
    20% 30%,
    10% 0%,
    40% 10%
  );
  -webkit-clip-path: polygon(
    45% 0%,
    60% 10%,
    90% 0%,
    80% 30%,
    100% 50%,
    80% 70%,
    90% 100%,
    60% 90%,
    45% 100%,
    40% 90%,
    10% 100%,
    20% 70%,
    0% 50%,
    20% 30%,
    10% 0%,
    40% 10%
  );
}

</style>
<style scoped>
/**
 * 商品庫存 / 已售出
 */
.stock-sold-wrapper {
  display: flex;
  gap: 20px;
  margin: 12px 0;

  .info-box {
    display: flex;
    align-items: center;
    padding: 6px 12px;
    border-radius: 6px;
    background: linear-gradient(120deg, #fff 0%, #f3f3f3 100%);
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
    transition: box-shadow 0.3s ease, background 0.3s ease;
    cursor: default;

    i {
      margin-right: 6px;
      font-size: 1.2rem;
      color: #444;
      transition: color 0.3s;
    }

    .number {
      font-size: 1.3rem;
      font-weight: bold;
      margin-right: 4px;
      color: #222;
    }

    .label {
      font-size: 0.85rem;
      color: #666;
    }

    &:hover {
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
      background: linear-gradient(120deg, #fafafa 0%, #f0f0f0 100%);

      i {
        color: #ff6600;
      }
    }
  }

  .sold-box {
    background: linear-gradient(120deg, #fff5f2 0%, #fce6e0 100%);

    &:hover {
      background: linear-gradient(120deg, #feece7 0%, #fbded6 100%);
    }

    i {
      color: #e74c3c;
    }
  }

  .stock-box {
    background: linear-gradient(120deg, #f5fff2 0%, #e4fce0 100%);

    &:hover {
      background: linear-gradient(120deg, #ebffe6 0%, #d5fcd2 100%);
    }

    i {
      color: #27ae60;
    }
  }
}
</style>